/**
 * withCapability: higher-order component that gates a child component on
 * a per-tenant capability flag. When the flag is disabled the wrapped
 * component renders nothing; when enabled it renders normally.
 *
 * Use this when you can't easily wrap a component at its call site (e.g.
 * the component is rendered by code we can't or don't want to touch in a
 * given wave). For new call sites, prefer the `CapabilityRoute` wrapper or
 * a `useCapability` check directly in the parent.
 *
 * Hook-order safety: the wrapper component is a separate render boundary,
 * so the capability check runs BEFORE the inner component's hooks. When
 * the flag is `false` the inner component is never instantiated, so its
 * own hooks are not added/removed mid-lifecycle. (A capability check inside
 * the inner component would risk a hook-order violation if the flag could
 * change between renders.)
 *
 * Avoids the work-when-hidden problem: the inner component's data fetches
 * never fire when the capability is off.
 *
 * @param {React.ComponentType} Component - Component to wrap.
 * @param {string} name - Capability flag name to gate on.
 * @returns {React.ComponentType} A new component that returns `null` when
 *   the capability is disabled, otherwise renders the original component
 *   with its props forwarded as-is.
 */
import React from 'react';

import useCapability from '@/shared/hooks/useCapability';

export default function withCapability(Component, name) {
    function CapabilityGated(props) {
        const enabled = useCapability(name);
        if (!enabled) return null;
        return <Component {...props} />;
    }
    const displayName = Component.displayName || Component.name || 'Component';
    CapabilityGated.displayName = `withCapability(${displayName}, ${name})`;
    return CapabilityGated;
}
